import React, { useContext, useEffect, useMemo } from "react";
import { CloseIcon } from "../../../atoms/icons/close";
import Button from "@mui/material/Button";
import Chart from "chart.js/auto";
import { InfoIcon } from "../../../atoms/icons/info-i";
import { dollarFormatter } from "../../../util/dollarFormatter";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { EditIcon } from "../../../atoms/icons/edit";
import { ChatReviewIcon } from "../../../atoms/icons/chat-review";
import { ShareIcon } from "../../../atoms/icons/share";
import { AccordionItems, ModalAccordion } from "./CustomAccordion";
import { DataCardMobile } from "./DataCardMobile";
import { useClosingCalculations } from "../../../util/hooks/useClosingCalculations";
import { getOneTwelfthValue } from "../../../util/getOneTwelfthValue";
import {
  InsuranceContainer,
  InsuranceDetailsContainer,
  InterestContainer,
  InterestDetailsContainer,
  PieChartDetailsMobileContainer,
  PmiContainer,
  PmiDetailsContainer,
  PmiTooltipPositioner,
  PrincipalContainer,
  PrincipalDetailsContainer,
  TaxesContainer,
  TaxesDetailsContainer,
} from "./ui-utils";
import { LookingToContext } from "../../../context/LookingToContext";
import { TooltipCombo } from "./TooltipCombo";
import { isRefinance } from "../../../util/compare_utils";

export const ViewDetailsMobileModalContent = React.forwardRef(function (
  {
    setShowDetailsModal,
    details,
    onEditClick,
    onShareClick,
    onReviewClick,
    taxes,
    insurance,
    earnestMoney,
    credits,
    insurancePaymentMonth,
    plannedClosingDate,
    hasWaivedEscrow,
    downPayment,
    content,
  },
  ref
) {
  const lookingTo = useContext(LookingToContext);

  const {
    sumOfCredits,
    sumOfEscrows,
    escrows,
    sumOfPrePaids,
    prePaids,
    sumOfPrimaryItems,
    primaryItems,
    sumOfGovFees,
    governmentFees,
    sumOfCannotShopFor,
    servicesBorrowerCannotShopFor,
    sumOfLenderFees,
    lenderFees,
    titleFees,
    lenderCredit,
    principal,
    interest,
    pmi,
    monthlyPayment,
    closingCosts,
    totalCashToClose,
  } = useClosingCalculations({
    details,
    downPayment,
    insurancePaymentMonth,
    plannedClosingDate,
    hasWaivedEscrow,
    insurance,
    taxes,
    credits,
    earnestMoney,
    lookingTo,
  });

  const pieChartData = useMemo(
    () => ({
      datasets: [
        {
          data: [
            principal,
            interest,
            pmi,
            getOneTwelfthValue(insurance),
            getOneTwelfthValue(taxes),
          ],
          backgroundColor: [
            "#205590",
            "#e7174b",
            "#600e2a",
            "#3a9ed7",
            "#666666",
          ],
          borderWidth: 0,
        },
      ],
    }),
    [principal, interest, pmi, insurance, taxes]
  );

  const chartCanvasRef = React.useRef();

  useEffect(() => {
    if (!chartCanvasRef.current) return;
    const chart = new Chart(chartCanvasRef.current, {
      type: "doughnut",
      data: pieChartData,
      options: {
        cutout: 0,
        radius: 50,
        plugins: { legend: { display: false } },
      },
    });

    return () => {
      chart.destroy();
    };
  }, [pieChartData, chartCanvasRef]);

  return (
    <div ref={ref} className="custom-modal custom-modal-mobile">
      <div className="left-column">
        <div className="top-row">Loan Summary</div>
        <div className="middle-row">
          <div className="data-card-sticky">
            <DataCardMobile quote={details} content={content} />
          </div>
          <div className="middle-row-bottom">
            <div className="middle-row-bottom-left">
              <div className="middle-row-title">Monthly Payment</div>
              <div className="left-title">
                <div className="whole-titles-wrapper">
                  <div className="titles-wrapper">
                    <PrincipalContainer>Principal:</PrincipalContainer>
                    <InterestContainer>Interest:</InterestContainer>
                    <PmiContainer>
                      PMI:
                      <PmiTooltipPositioner>
                        <TooltipCombo tip={content.tooltip1} />
                      </PmiTooltipPositioner>
                    </PmiContainer>
                    <InsuranceContainer>Insurance:</InsuranceContainer>
                    <TaxesContainer>Taxes:</TaxesContainer>
                  </div>
                  <div className="titles-wrapper">
                    <PrincipalDetailsContainer>
                      {`${dollarFormatter(principal, 2)}`}
                    </PrincipalDetailsContainer>
                    <InterestDetailsContainer>
                      {`${dollarFormatter(interest, 2)}`}
                    </InterestDetailsContainer>
                    <PmiDetailsContainer>
                      {`${dollarFormatter(pmi, 2)}`}
                    </PmiDetailsContainer>
                    <InsuranceDetailsContainer>
                      {`${dollarFormatter(
                        Math.round(Number(insurance ?? 0) / 12)
                      )}`}
                      <IconButton
                        aria-label="Edit loan selections"
                        onClick={onEditClick}
                      >
                        <EditIcon fill="#ddd" width="16px" height="16px" />
                      </IconButton>
                    </InsuranceDetailsContainer>
                    <TaxesDetailsContainer>
                      {`${dollarFormatter(
                        Math.round(Number(taxes ?? 0) / 12)
                      )}`}
                      <IconButton
                        aria-label="Edit loan selections"
                        onClick={onEditClick}
                      >
                        <EditIcon fill="#ddd" width="16px" height="16px" />
                      </IconButton>
                    </TaxesDetailsContainer>
                  </div>
                </div>
                <PieChartDetailsMobileContainer>
                  <canvas ref={chartCanvasRef} />
                </PieChartDetailsMobileContainer>
                <div className="i-icon">
                  <InfoIcon
                    className="margin-right"
                    fill="lightgrey"
                    width="20px"
                    height="20px"
                  />
                </div>
              </div>
              <div className="total-closing">
                <div>Total Monthly Payments:</div>
                {dollarFormatter(
                  monthlyPayment +
                    Number(taxes ?? 0) / 12 +
                    Number(insurance ?? 0) / 12,
                  2
                )}
              </div>
            </div>
            <div className="middle-row-bottom-middle">
              <div className="middle-row-title">Closing Costs</div>
              <div className="middle-title">
                <ModalAccordion
                  tooltipText={content.tooltip12}
                  title="Lender Fees"
                  amount={sumOfLenderFees}
                >
                  <div className="accordion-children">
                    {lenderFees.map((item) => {
                      return (
                        <AccordionItems
                          title={item.title}
                          amount={item.amount}
                          key={item.title}
                        />
                      );
                    })}
                  </div>
                </ModalAccordion>
                <ModalAccordion
                  title="Services Borrower Cannot Shop For"
                  add
                  amount={sumOfCannotShopFor}
                  tooltipText={content.tooltip13}
                >
                  <div className="accordion-children">
                    {servicesBorrowerCannotShopFor.map((item) => {
                      return (
                        <AccordionItems
                          title={item.title}
                          amount={item.amount}
                          key={item.title}
                        />
                      );
                    })}
                  </div>
                </ModalAccordion>
                <ModalAccordion
                  title="Services Borrower Can Shop For"
                  add
                  amount={titleFees}
                  tooltipText={content.tooltip14}
                >
                  <div className="accordion-children">
                    <AccordionItems
                      title="Title Fees"
                      amount={titleFees}
                      key="Title Fees"
                    />
                  </div>
                </ModalAccordion>
                <ModalAccordion
                  title="Government Fees"
                  add
                  amount={sumOfGovFees}
                  tooltipText={content.tooltip15}
                >
                  <div className="accordion-children">
                    {governmentFees.map((item) => {
                      return (
                        <AccordionItems
                          title={item.title}
                          amount={item.amount}
                          key={item.title}
                        />
                      );
                    })}
                  </div>
                </ModalAccordion>
                <ModalAccordion
                  subtract={!!lenderCredit}
                  add={!lenderCredit}
                  title="Lender Credit"
                  amount={Math.abs(lenderCredit)}
                  disabled
                  tooltipText={content.tooltip16}
                />
                <div className="total-closing">
                  <div>Total Closing Costs:</div>
                  {dollarFormatter(Math.round(closingCosts))}
                </div>
              </div>
            </div>
            <div className="middle-row-bottom-right">
              <div className="middle-row-title">Cash to Close</div>
              <div className="middle-title">
                <ModalAccordion
                  title="Primary Items"
                  amount={sumOfPrimaryItems}
                  tooltipText={content.tooltip17}
                >
                  <div className="accordion-children">
                    {primaryItems.map((item) =>
                      item.title === "Down Payment" &&
                      lookingTo === "Refinance" ? null : (
                        <AccordionItems
                          title={item.title}
                          amount={item.amount}
                          key={item.title}
                        />
                      )
                    )}
                  </div>
                </ModalAccordion>
                <ModalAccordion
                  title="Prepaids"
                  add
                  amount={sumOfPrePaids}
                  tooltipText={content.tooltip18}
                >
                  <div className="accordion-children">
                    {prePaids.map((item) => {
                      return (
                        <AccordionItems
                          title={item.title}
                          amount={item.amount}
                          key={item.title}
                        />
                      );
                    })}
                  </div>
                </ModalAccordion>
                <ModalAccordion
                  title="Escrows"
                  add
                  amount={sumOfEscrows}
                  tooltipText={content.tooltip19}
                >
                  <div className="accordion-children">
                    {escrows.map((item) => {
                      return (
                        <AccordionItems
                          title={item.title}
                          amount={item.amount}
                          key={item.title}
                        />
                      );
                    })}
                  </div>
                </ModalAccordion>
                {!isRefinance() && (
                  <ModalAccordion
                    title="Credits"
                    subtract
                    amount={sumOfCredits}
                    tooltipText={content.tooltip20}
                  >
                    <div className="accordion-children">
                      <AccordionItems
                        title="Earnest Money"
                        amount={earnestMoney}
                        key="Earnest Money"
                      />
                      <AccordionItems
                        title="Seller/Realtor Credit"
                        amount={credits}
                        key="Seller/Realtor Credit"
                      />
                    </div>
                  </ModalAccordion>
                )}
                <div className="total-closing">
                  <div>Total Cash to Close:</div>
                  {dollarFormatter(Math.round(totalCashToClose))}
                </div>
                <Stack gap={1} mt={4}>
                  <Button
                    variant="contained"
                    sx={{
                      boxShadow: "none",
                      height: "46px",
                      borderRadius: "8px",
                      backgroundColor: "#e2e2e2",
                      color: "#323a51",
                      paddingLeft: "40px",
                      paddingRight: "40px",
                      display: "flex",
                      alignItems: "center",
                      fontFamily: "proxima-nova, sans-serif",
                      fontWeight: "bold",
                      fontSize: "16px",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#868c99",
                      },
                    }}
                    startIcon={
                      <EditIcon fill="#323a51" width="16" height="16" />
                    }
                    onClick={onEditClick}
                  >
                    Edit cash to close
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      height: "46px",
                      borderRadius: "8px",
                      boxShadow: "none",
                      backgroundColor: "#e2e2e2",
                      color: "#323a51",
                      paddingLeft: "40px",
                      paddingRight: "40px",
                      display: "flex",
                      alignItems: "center",
                      textTransform: "none",
                      fontFamily: "proxima-nova, sans-serif",
                      fontWeight: "bold",
                      fontSize: "16px",
                      "&:hover": {
                        backgroundColor: "#868c99",
                      },
                    }}
                    startIcon={
                      <ShareIcon fill="#323a51" width="20" height="20" />
                    }
                    onClick={onShareClick}
                  >
                    Share this loan option
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      height: "46px",
                      borderRadius: "8px",
                      boxShadow: "none",
                      fontFamily: "proxima-nova, sans-serif",
                      fontWeight: "bold",
                      fontSize: "16px",
                      backgroundColor: "#e2e2e2",
                      color: "#323a51",
                      paddingLeft: "40px",
                      paddingRight: "40px",
                      display: "flex",
                      alignItems: "center",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#868c99",
                      },
                    }}
                    startIcon={
                      <ChatReviewIcon fill="#323a51" width="24" height="24" />
                    }
                    onClick={onReviewClick}
                  >
                    Review with a loan officer
                  </Button>
                </Stack>
              </div>
            </div>
          </div>
        </div>
        <div className="bottom-row">
          <Button
            variant="contained"
            sx={{
              borderRadius: "20px",
              backgroundColor: "#00e09d",
              color: "white",
              paddingLeft: "50px",
              paddingRight: "50px",
              textTransform: "none",
              flex: 1,
              fontFamily: "proxima-nova, sans-serif",
              fontWeight: "bold",
              fontSize: "16px",
              "&:hover": {
                backgroundColor: "#009b62",
                color: "white",
              },
            }}
            href="/apply"
          >
            Apply Now
          </Button>
        </div>
      </div>

      <div className="close-button" onClick={() => setShowDetailsModal(false)}>
        <CloseIcon />
      </div>
    </div>
  );
});
